import Section from "@Components/Section";
import { AppState } from "@Store/index";
import { MICRO_DATA_SEO_TEST_PAGES_SLUG } from "constants/configConstants";
import getRegionHeading from "helpers/getRegionHeadings";
import { useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import FAQs from "../FAQ";
import styles from "./sectionRegionInfo.module.scss";
import SectionContainer from "@Pages/PropertyPage/components/SectionContainer";
import Seprator from "@Components/Seprator";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import config from "@Config/index";
import REGION_WORDS from "./constants";
import NearbySection from "../NearbySection";
import NearbyPlaces from "@Pages/PropertyPage/components/NearbyPlaces";
import useIsApp from "hooks/useIsApp";
import useIsPaginatedSearchPage from "@Pages/Search/hooks/useIsPaginatedSearchPage";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";

type SectionRegionInfoTypes = {
  includeFields?: string[];
  isDesktop?: boolean;
  isSearchPage?: boolean;
  isPropertyPage?: boolean;
  defaultFirstOpen?: boolean;
  excludeQuestions?: string[];
  smartQuestions?: string[];
};

const getRegionData = (propertyPageData: any, searchPageData: any, isPropertyPage: boolean) => {
  if (isPropertyPage) {
    return propertyPageData?.data?.region || searchPageData?.region;
  }
  return searchPageData?.region || propertyPageData?.data?.region;
};

const SectionRegionInfo = ({
  includeFields = [],
  isDesktop = false,
  isSearchPage = false,
  isPropertyPage = false,
  defaultFirstOpen = false,
  excludeQuestions = [],
  smartQuestions,
}: SectionRegionInfoTypes) => {
  const { region, inventory, features } = useSelector(
    ({ propertyPageData, searchPageData }: AppState) => ({
      region: getRegionData(propertyPageData, searchPageData, isPropertyPage),
      inventory: propertyPageData?.data,
      features: propertyPageData?.data?.features,
    }),
    shallowEqual,
  );
  const isFAQSearchEnable = true;
  const featuresAllValue = features?.flatMap((feature: { values: any[] }) =>
    Array.isArray(feature.values) ? feature.values : [],
  );
  const isSeoTestPageRef = useRef(MICRO_DATA_SEO_TEST_PAGES_SLUG.includes(region?.canonical_name));
  const { isWhitelabel } = useWhitelabel();
  const regionHeading = getRegionHeading(region, true);
  const isMiniProgram = useIsMiniProgram();
  const isApp = useIsApp();
  const isPaginatedSearchPage = useIsPaginatedSearchPage();
  // GrowthBook never initialises in China, so the section shows there unflagged.
  const showRegionDescription =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SEARCH_REGION_DESCRIPTION_SECTION) ||
    config.IS_CHINA_APP;

  const includeField = (field_name: string) => {
    let flag = true;
    if (includeFields.length > 0) flag = includeFields.includes(field_name);

    if (isPropertyPage && field_name === "description") {
      flag = false;
    }

    return flag;
  };

  const formatDescriptionData = () => {
    let data = region?.description?.map((item: any) => ({
      question: item.display_name,
      answer: item.value,
    }));
    if (config.IS_CHINA_APP && !isMiniProgram && !isApp && region?.region_type === "locality") {
      data = data?.slice(1); // removing first as it is shown below the h1
    }
    return data;
  };
  const { t } = useTranslationHook();

  const headingText = config.IS_CHINA_APP
    ? t("propertypage.sectionregioninfo.studentaccommodationsinstring")
    : t(
        `${t("propertypage.sectionregioninfo.studentstring")} ${
          REGION_WORDS[region?.root_name] ||
          t("propertypage.sectionregioninfo.accommodationsstring")
        } ${
          region?.region_type === t("propertypage.sectionregioninfo.establishmentstring") ||
          region?.region_type === t("propertypage.sectionregioninfo.sublocalitystring")
            ? t("propertypage.sectionregioninfo.nearstring")
            : t("propertypage.sectionregioninfo.instring")
        }`,
      );

  const formattedDescriptionData = formatDescriptionData();

  if (isMiniProgram)
    return (
      <>
        {" "}
        {includeField("inventoryFaqs") && inventory?.faqs?.length > 0 && (
          <SectionContainer
            title={t("propertypage.sectionregioninfo.faqstitlestring")}
            dataTestId="search-inventory-faq-section"
            id="faqs"
            headingLevel={2}
            {...(isDesktop
              ? { className: formatClassNames(styles.desktop, styles.desktopSectionContainer) }
              : {})}
          >
            <FAQs
              data={inventory?.faqs}
              propertyPageGtagEvents
              isSeoTestPage={isSeoTestPageRef.current}
              dataTestId="faq"
              addSearchBar={isMiniProgram}
              defaultFirstOpen={defaultFirstOpen}
              useSmartSearch={!isMiniProgram}
              excludeQuestions={excludeQuestions}
              smartQuestions={smartQuestions}
            />
            {!isDesktop && <Seprator noMargin />}
          </SectionContainer>
        )}
      </>
    );

  // Paginated search URLs (?p=2 and beyond) skip these SEO sections — the FAQs,
  // the region description and the nearby-places links belong on page 1 only,
  // instead of being repeated on every page of the same listing.
  if (isSearchPage && isPaginatedSearchPage) return null;

  return (
    <section
      className={formatClassNames(
        isPropertyPage ? styles.ppContainer : styles.container,
        isDesktop && isSearchPage ? styles.containerDesktop : "",
        isWhitelabel && styles.sectionContainerWhitelabel,
      )}
    >
      {/* General FAQs */}
      {includeField("faqs") && region?.faqs?.length > 0 && !isApp && (
        <Section
          title={t("propertypage.sectionregioninfo.faqstitlestring")}
          dataTestId="searchPage-faq-section"
          id="search-faq-section"
          withSeprator={false}
          {...(isDesktop
            ? { className: styles.desktopSectionContainer }
            : { className: styles.mobileCardContainer })}
        >
          <FAQs
            data={region?.faqs}
            dataAmenities={featuresAllValue}
            isSeoTestPage={isSeoTestPageRef.current}
            dataTestId="faq"
            isDesktop={isDesktop}
            addSearchBar={!isSearchPage && isFAQSearchEnable}
          />
        </Section>
      )}
      {/* Region level FAQs ("Student Accommodations in {City}"), behind a flag for SEO (P1D-5539) */}
      {includeField("description") &&
        showRegionDescription &&
        formattedDescriptionData?.length > 0 &&
        !isApp && (
          <Section
            title={`${headingText} ${regionHeading}`}
            dataTestId="searchPage-description-section"
            id="search-description-section"
            withSeprator={false}
            showTitleLine
            {...(isDesktop
              ? { className: styles.desktopSectionContainer }
              : { className: styles.mobileCardContainer })}
          >
            <FAQs
              data={formattedDescriptionData}
              dataAmenities={featuresAllValue}
              dataTestId="studentAccomodation"
              isDesktop={isDesktop}
            />
          </Section>
        )}
      {includeField("links") && !isWhitelabel && !isApp && (
        <Section
          title={t("propertypage.sectionregioninfo.nearbyplacesstring")}
          dataTestId="searchPage-nearbyPlacesToVisit-section"
          id={isPropertyPage ? "property-nearby-section" : "search-nearby-section"}
          withSeprator={false}
          showTitleLine
          {...(isDesktop
            ? {
                className: isPropertyPage
                  ? styles.desktopSectionContainerPropertyPage
                  : styles.desktopSectionContainer,
              }
            : { className: styles.mobileCardContainer })}
        >
          {isPropertyPage ? (
            <NearbyPlaces isDesktop={isDesktop} />
          ) : (
            <NearbySection isDesktop={isDesktop} />
          )}
        </Section>
      )}
      {/* Temporarily disabled as per SEO Team's request */}
      {/* {isSearchPage && <FatFooter isDesktop={isDesktop} />} */}
      {includeField("inventoryFaqs") && inventory?.faqs?.length > 0 && (
        <SectionContainer
          title={t("propertypage.sectionregioninfo.faqsshorttitlestring")}
          dataTestId="search-inventory-faq-section"
          id="faqs"
          headingLevel={2}
          {...(isDesktop
            ? { className: formatClassNames(styles.desktop, styles.desktopSectionContainer) }
            : { className: styles.mobileFAQContainer })}
          showTitleLine
        >
          <FAQs
            data={inventory?.faqs}
            dataAmenities={featuresAllValue}
            propertyPageGtagEvents
            isSeoTestPage={isSeoTestPageRef.current}
            dataTestId="faq"
            isDesktop={isDesktop}
            addSearchBar={config?.IS_CHINA_APP || (!isSearchPage && isFAQSearchEnable)}
            useSmartSearch={!config?.IS_CHINA_APP}
            defaultFirstOpen={defaultFirstOpen}
            excludeQuestions={excludeQuestions}
            smartQuestions={smartQuestions}
          />
        </SectionContainer>
      )}
    </section>
  );
};

export default wrapperHOC(SectionRegionInfo, {
  componentName: "SectionRegionInfo-SearchPage",
  showForChina: true,
});
